import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowRight,
  BadgeCheck,
  Menu,
  QrCode,
  ShieldCheck,
  Smartphone,
} from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      await login(formData.email.trim().toLowerCase(), formData.password);

      toast.success("Tekrar hoş geldiniz!");
      navigate("/admin");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Giriş başarısız. Bilgilerinizi kontrol edin."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#FCFBF9] text-zinc-950">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-zinc-200/70 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[14px] bg-[#EA580C] text-white shadow-[0_10px_30px_rgba(234,88,12,0.22)]">
              <Menu className="h-5 w-5" />
            </div>

            <div className="min-w-0 leading-tight">
              <div className="truncate text-[15px] font-extrabold tracking-[-0.02em] text-zinc-950">
                Web Menü
              </div>
              <div className="mt-0.5 truncate text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                Megriva teknolojisiyle
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="hidden rounded-full px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-950 sm:inline-flex"
            >
              Ana Sayfa
            </Link>

            <Link
              to="/register"
              className="inline-flex h-10 items-center justify-center rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800"
            >
              Kayıt Ol
            </Link>
          </div>
        </div>
      </header>

      <div aria-hidden="true" className="h-[72px]" />

      <main className="relative">
        <div
          aria-hidden="true"
          className="absolute left-[-180px] top-[-120px] h-[420px] w-[420px] rounded-full bg-orange-200/35 blur-[110px]"
        />
        <div
          aria-hidden="true"
          className="absolute right-[-180px] top-[120px] h-[420px] w-[420px] rounded-full bg-amber-100/60 blur-[110px]"
        />

        <div className="relative mx-auto grid min-h-[calc(100vh-72px)] max-w-7xl items-stretch lg:grid-cols-[1.02fr_0.98fr]">
          <section className="hidden border-r border-zinc-200/70 px-8 py-12 lg:flex lg:flex-col lg:justify-between xl:px-14 xl:py-14">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-4 py-2 text-xs font-bold text-orange-700 shadow-sm">
                <BadgeCheck className="h-4 w-4" />
                İşletmenizin dijital vitrini
              </div>

              <h1 className="mt-7 text-[52px] font-extrabold leading-[1.03] tracking-[-0.045em] text-zinc-950 xl:text-[62px]">
                İşletmenizi{" "}
                <span className="text-[#EA580C]">tek panelden</span>{" "}
                yönetin.
              </h1>

              <p className="mt-6 max-w-lg text-base font-medium leading-8 text-zinc-600">
                Ürünlerinizi, hizmetlerinizi, kampanyalarınızı ve işletme
                bilgilerinizi Web Menü ile güncel tutun. QR kodunuz ve özel
                bağlantınız her zaman aynı yerde kalsın.
              </p>

              <div className="mt-9 grid max-w-lg gap-3">
                {[
                  {
                    icon: QrCode,
                    title: "Tek QR, güncel içerik",
                    text: "İçeriğiniz değişse bile QR kodunuzu yeniden basmanız gerekmez.",
                  },
                  {
                    icon: Smartphone,
                    title: "Web ve mobil yönetim",
                    text: "İşletmenizi masa başında veya hareket halindeyken yönetin.",
                  },
                  {
                    icon: ShieldCheck,
                    title: "Sade ve güvenli erişim",
                    text: "Yönetim alanınıza hesabınızla giriş yapın ve içeriklerinizi yönetin.",
                  },
                ].map((item) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={item.title}
                      className="flex gap-4 rounded-[20px] border border-zinc-200/80 bg-white/80 p-4 shadow-[0_10px_30px_rgba(24,24,27,0.04)] backdrop-blur"
                    >
                      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-orange-50 text-[#EA580C]">
                        <Icon className="h-5 w-5" />
                      </div>

                      <div>
                        <p className="text-sm font-extrabold text-zinc-900">
                          {item.title}
                        </p>
                        <p className="mt-1 text-xs font-medium leading-5 text-zinc-500">
                          {item.text}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-10 flex items-center gap-3 text-xs font-semibold text-zinc-400">
              <span className="h-px w-10 bg-zinc-300" />
              Web Menü • Megriva
            </div>
          </section>

          <section className="flex items-center justify-center px-4 py-10 sm:px-6 lg:px-10 xl:px-16">
            <div className="w-full max-w-[470px]">
              <div className="mb-8 lg:hidden">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-3.5 py-2 text-[11px] font-bold text-orange-700 shadow-sm">
                  <BadgeCheck className="h-4 w-4" />
                  Web Menü yönetim alanı
                </div>
              </div>

              <div className="rounded-[30px] border border-zinc-200/80 bg-white p-5 shadow-[0_28px_80px_rgba(24,24,27,0.08)] sm:p-8">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#EA580C]">
                    Giriş Yap
                  </p>

                  <h2
                    className="mt-3 text-3xl font-extrabold tracking-[-0.035em] text-zinc-950 sm:text-[38px]"
                    data-testid="login-heading"
                  >
                    Tekrar hoş geldiniz.
                  </h2>

                  <p className="mt-3 text-sm font-medium leading-6 text-zinc-500">
                    Web Menü yönetim panelinize devam etmek için hesap
                    bilgilerinizi girin.
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="mt-8 space-y-5"
                  data-testid="login-form"
                >
                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="text-xs font-bold text-zinc-700"
                    >
                      E-posta Adresi
                    </Label>

                    <Input
                      id="email"
                      type="email"
                      placeholder="ornek@isletmeniz.com"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      required
                      autoComplete="email"
                      className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 text-sm focus-visible:ring-orange-200"
                      data-testid="login-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <Label
                        htmlFor="password"
                        className="text-xs font-bold text-zinc-700"
                      >
                        Şifre
                      </Label>

                      <button
                        type="button"
                        onClick={() => navigate("/forgot-password")}
                        className="text-xs font-bold text-[#EA580C] transition hover:text-[#C2410C]"
                      >
                        Şifremi unuttum
                      </button>
                    </div>

                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      required
                      autoComplete="current-password"
                      className="h-12 rounded-[14px] border-zinc-200 bg-zinc-50/70 px-4 text-sm focus-visible:ring-orange-200"
                      data-testid="login-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="h-12 w-full rounded-full bg-[#EA580C] text-sm font-bold text-white shadow-[0_14px_32px_rgba(234,88,12,0.22)] transition hover:bg-[#C2410C] active:scale-[0.99]"
                    disabled={loading}
                    data-testid="login-submit-button"
                  >
                    {loading ? (
                      "Giriş yapılıyor..."
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        Giriş Yap
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    )}
                  </Button>
                </form>

                <div className="mt-7 border-t border-zinc-100 pt-6">
                  <p className="text-center text-sm font-medium text-zinc-500">
                    Henüz bir hesabınız yok mu?{" "}
                    <Link
                      to="/register"
                      className="font-bold text-[#EA580C] transition hover:text-[#C2410C]"
                      data-testid="login-register-link"
                    >
                      Hesap oluşturun
                    </Link>
                  </p>
                </div>
              </div>

              <p className="mt-5 text-center text-[11px] font-medium leading-5 text-zinc-400">
                Giriş yaparak Web Menü yönetim alanınıza güvenli şekilde
                erişirsiniz.
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
